// Central business info for Oakford Ledger.
export const SITE = {
  name: "Oakford Ledger",
  founder: "Michael",
  mission: "Our mission is to help small businesses grow strong through accurate bookkeeping and financial clarity—rooted in integrity, built to endure.",
  email: "michael@oakfordledger.com",
  phoneDisplay: "(619) 467-0797",
  phoneHref: "tel:+16194670797",
  smsHref: "sms:+16194670797",
  phoneLabel: "Call or text",
  addressLabel: "Business Mailing Address",
  addressLines: ["4539 N 22nd St Ste N", "Phoenix, AZ 85016", "United States"],
  hours: [
    { d: "Monday – Friday", t: "6:00 PM – 9:30 PM" },
    { d: "Saturday", t: "9:00 AM – 5:00 PM" },
  ],
  timezone: "Arizona time (America/Phoenix)",
  booking: "https://calendar.app.google/wMBzeYanGin1vkvKA",
  facebook: "https://www.facebook.com/oakfordledger",
  logo: "/assets/oakford-emblem.png",
  serviceArea: "Based in Arizona. Providing remote bookkeeping services to small businesses across the United States.",
  serviceAreaShort: "Remote bookkeeping for small businesses nationwide.",
  footerTagline: "Remote QuickBooks bookkeeping | Serving small businesses nationwide",
};

// The only two stock photos on the site (owner-selected, Unsplash License). Credits: /public/assets/photos/CREDITS.md
// Each is a 3:2 crop served as WebP at 550 / 825 / 1100 px.
export const PHOTOS = {
  homePlanner: { base: "/assets/photos/home-planner", alt: "A person writing in a weekly planner beside a coffee cup and laptop" },
  servicesDesk: { base: "/assets/photos/services-desk", alt: "A laptop, pen, and paperwork laid out on a wooden desk" },
};

export const SERVICES = [
  { id: "monthly", num: "01", title: "Monthly Bookkeeping", desc: "We record and categorize your income and expenses each month in QuickBooks, so your books stay current and organized without you having to chase them." },
  { id: "reconciliation", num: "02", title: "Bank & Credit Card Reconciliation", desc: "We match your bank and credit card activity against your records to catch missing or duplicate transactions and keep your balances accurate." },
  { id: "reporting", num: "03", title: "Financial Reporting", desc: "We prepare clear profit & loss and balance sheet reports so you can see how your business is doing and make decisions with confidence." },
];

// Home "benefits" strip — what you gain (distinct from the service descriptions above).
export const BENEFITS = [
  { t: "Books That Stay Current", d: "Each month closes on schedule, so you are never catching up at tax time." },
  { t: "Fewer Surprises", d: "Reconciled accounts surface missing, duplicate, or unexpected charges early." },
  { t: "Decisions With Context", d: "Monthly statements give you a reliable picture before you spend or hire." },
  { t: "Time Back", d: "Hours spent sorting transactions go back into running your business." },
];

// About page principles (kept distinct from BENEFITS on Home).
export const PRINCIPLES = [
  { num: "01", title: "Consistency", desc: "The same careful method applied every month, so your records are comparable year over year." },
  { num: "02", title: "Clear Communication", desc: "Plain-language updates and answers to your questions — no jargon, no guesswork." },
  { num: "03", title: "Confidentiality", desc: "Your financial information is handled with care and shared only with the people you authorize." },
  { num: "04", title: "Follow-Through", desc: "Commitments kept: reports delivered on the agreed schedule, questions answered." },
];

export const BIO = {
  heading: "Meet Michael",
  role: "Founder of Oakford Ledger",
  paragraphs: [
    "I’m Michael, the founder of Oakford Ledger. My background is in personal banking, and I’m currently studying business management. My interest in entrepreneurship led me to build a business focused on helping small business owners keep their financial records organized and understand their numbers.",
    "My approach is straightforward: careful bookkeeping, clear communication, and personal attention. When you work with Oakford Ledger, you work directly with me.",
    "Two accomplishments that have shaped my life are earning the rank of Eagle Scout and serving a two-year mission in England. Those experiences strengthened my appreciation for service, preparation, perseverance, and following through on commitments—values I bring to building Oakford Ledger.",
    "My goal is to provide dependable bookkeeping support that gives you a clearer picture of your business and more time to focus on running it.",
  ],
  portrait: null, // set to "/assets/michael-portrait.webp" once the owner's photo is supplied — never a stock or generated person
};

// Training & credentials — authentic badge artwork supplied by the owner (null until the files arrive).
export const CREDENTIALS = [
  // Labels and status mirror the wording printed on each authentic Intuit badge.
  { id: "cas", label: "Client Advisory Services Foundations", status: "Intuit ProAdvisor · Graduate", image: "/assets/badge-cas-foundations.png" },
  { id: "qb1", label: "QuickBooks Level 1", status: "Intuit ProAdvisor · Certified", image: "/assets/badge-quickbooks-level-1.png" },
  { id: "bk", label: "Intuit Bookkeeping", status: "Intuit · Trained", image: "/assets/badge-intuit-bookkeeping.png" },
];

// Fixed monthly bookkeeping packages — exact prices, limits, and wording supplied by the owner. Do not add extras.
// Core services included in ALL THREE packages (listed once above the cards; summarized on each card as "Complete monthly bookkeeping").
export const CORE_SERVICES = [
  "Monthly transaction categorization",
  "Bank and credit card reconciliation",
  "Receipt organization and matching",
  "Monthly profit-and-loss statement and balance sheet",
  "Brief written monthly summary",
  "Year-end bookkeeping reports for the client’s tax preparer",
];

export const PACKAGES = [
  {
    id: "essentials",
    name: "Essentials",
    price: "$250",
    label: "Essentials Package — $250/month",
    available: true,
    cta: "Request Essentials",
    tagline: "Complete monthly bookkeeping",
    limits: ["Up to 50 monthly transactions", "Up to 2 bank or credit card accounts combined"],
    features: ["All core services: categorization, reconciliation, receipt matching, monthly statements, written summary, and year-end reports"],
  },
  {
    id: "standard",
    name: "Standard",
    price: "$400",
    label: "Standard Package — $400/month",
    available: true,
    cta: "Request Standard",
    tagline: "Complete monthly bookkeeping, plus review and cash-flow insight",
    limits: ["Up to 100 monthly transactions", "Up to 3 bank or credit card accounts combined"],
    features: [
      "Everything in Essentials",
      "One 30-minute monthly report review",
      "Monthly cash-flow statement",
      "Prior-month income and expense comparisons, when prior-period records are available",
    ],
  },
  {
    id: "full",
    name: "Full Service",
    price: "$600",
    label: "Full Service Package — $600/month",
    available: false, // COMING SOON — not selectable anywhere (cards, contact form, URL params)
    cta: "Coming Soon",
    tagline: "Complete monthly bookkeeping, plus invoicing and bill tracking",
    limits: ["Up to 200 monthly transactions", "Up to 4 bank or credit card accounts combined"],
    features: [
      "Everything in Standard",
      "Create and send up to 10 customer invoices monthly, track payments, and send routine overdue reminders",
      "Enter and track up to 10 vendor bills monthly and provide a list of bills due",
      "Actual bill payment is excluded",
    ],
  },
];

// Packages a visitor may actually choose (cards, contact form, ?package= preselection).
export const SELECTABLE_PACKAGES = PACKAGES.filter((p) => p.available);

// Service-by-service comparison: [label, essentials, standard, full]. true = included, false = not included.
export const COMPARISON = [
  ["Monthly transactions (all included accounts)", "Up to 50", "Up to 100", "Up to 200"],
  ["Bank or credit card accounts (combined)", "Up to 2", "Up to 3", "Up to 4"],
  ["Monthly transaction categorization", true, true, true],
  ["Bank and credit card reconciliation", true, true, true],
  ["Receipt organization and matching", true, true, true],
  ["Monthly profit-and-loss statement and balance sheet", true, true, true],
  ["Brief written monthly summary", true, true, true],
  ["Year-end bookkeeping reports for your tax preparer", true, true, true],
  ["30-minute monthly report review", false, true, true],
  ["Monthly cash-flow statement", false, true, true],
  ["Prior-month income and expense comparisons", false, true, true],
  ["Customer invoicing (up to 10/month), payment tracking, overdue reminders", false, false, true],
  ["Vendor bills entered and tracked (up to 10/month) with bills-due list", false, false, true],
  ["Actual bill payment", false, false, false],
];

// Not part of any package, now or in the Full Service preview. No prices, no selection.
export const FUTURE_ADDITIONAL_SERVICES = ["Payroll processing", "Quarterly advisory reviews"];

export const PACKAGE_NOTES = [
  "Monthly transaction limits apply across all included accounts combined.",
  "Account limits combine bank and credit card accounts.",
  "QuickBooks and any other software subscriptions are paid separately by the client.",
  "Cleanup, catch-up, initial setup, and payment-processor reconciliation require a separate review and quote.",
  "Additional accounts, volume, invoices, bills, or complexity require an agreed scope and fee adjustment.",
  "Customer invoicing, payment tracking, and vendor-bill tracking are planned Full Service features and are not currently available. Actual bill payment is excluded.",
  "Payroll processing and quarterly advisory reviews are future additional services and are not currently available.",
  "Tax preparation, income-tax filing, sales-tax filing, and inventory management are not included.",
  "The engagement agreement confirms the services, responsibilities, monthly fee, and reporting schedule.",
];
